<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, inject, ref, Ref } from 'vue'
import { useFieldArray, useFormValues } from 'vee-validate'
import * as apisPortal from '/@src/client-portal/api'
import { useNotyf } from '/@src/composable/useNotyf'
import { useZoppm } from '/@src/composable/useZoppm'
import { Dokument } from '/@src/client-portal'
import Modal from '/@src/pages/elements/modal.vue'

const STRUCTURE_PATH = 'prilohy'

const { t } = useI18n()
const notif = useNotyf()
const api = new apisPortal.ZoppmDraftApi()
const kodIrz = inject<Ref<string>>('kodIrz')
const isReadOnly = inject<boolean>('isReadOnly')
const zoppm = !isReadOnly ? useZoppm() : null
const formValues = useFormValues()
const defaultValueWhenEmpty = ref<string>('Názov prílohy')
const { push, remove, fields } = useFieldArray(STRUCTURE_PATH)

async function handleFileRemove(identifikatorUlozisko: string, dokumenty: Dokument[]) {
  const verziaUlozisko = dokumenty?.find(
    (file) => file?.dokumentRef?.identifikatorUlozisko === identifikatorUlozisko
  )?.dokumentRef?.verziaUlozisko

  // TODO: na remove, odstranit subor aj z VeeValidate a zavolat `handleUpdateDraft`
  if (kodIrz?.value) {
    try {
      await api
        .deleteContentOfDokumentForZoppmDraftZoppmDraft(
          kodIrz.value,
          identifikatorUlozisko,
          verziaUlozisko
        )
        .then(() => {
          notif.success('Súbor zmazaný')
        })
      await zoppm?.handleUpdateDraft(kodIrz.value, formValues.value)
    } catch {
      notif.error('Nastala chyba')
    }
  }
}

async function handleRemovePrilohy(idx: number) {
  const priloha = prilohy.value[idx]

  if (priloha) {
    const dokumenty = priloha.priloha?.dokumenty

    if (dokumenty) {
      for (const dokument of dokumenty) {
        if (dokument) {
          handleFileRemove(dokument.dokumentRef?.identifikatorUlozisko, dokumenty)
        }
      }
    }

    remove(idx)
  }
}

const prilohy = computed(() => formValues.value?.prilohy)
</script>

<template>
  <IDSKHeading as="h3" variant="l">{{
    t('zoppm.step13.attachment3.mainHeading')
  }}</IDSKHeading>
  <IDSKParagraph>{{ t('zoppm.step13.attachment3.mainInfo') }}</IDSKParagraph>

  <template v-if="!isReadOnly">
    <template v-for="(priloha, idx) in prilohy" :key="idx">
      <div v-if="!priloha?.povinnaPrilohaRef?.zoppmSpecId" class="govuk-inset-text">
        <ValidationWrapper
          v-slot="slotProps"
          :name="`${STRUCTURE_PATH}[${idx}].priloha.poradie`"
          :value="idx + 1"
        >
          <IDSKInput
            :id="`${STRUCTURE_PATH}[${idx}].priloha.poradie`"
            v-bind="slotProps"
            readonly
            hidden
          />
        </ValidationWrapper>
        <ValidationWrapper
          v-slot="slotProps"
          :name="`${STRUCTURE_PATH}[${idx}].priloha.lokalnaReferencia.refSekcia`"
          value="DALSIE_PRILOHY"
        >
          <IDSKInput
            :id="`${STRUCTURE_PATH}[${idx}].priloha.lokalnaReferencia.refSekcia`"
            v-bind="slotProps"
            readonly
            hidden
          />
        </ValidationWrapper>
        <ValidationWrapper
          v-slot="slotProps"
          :name="`${STRUCTURE_PATH}[${idx}].priloha.lokalnaReferencia.refId`"
          value="DALSIE_PRILOHY"
        >
          <IDSKInput
            :id="`${STRUCTURE_PATH}[${idx}].priloha.lokalnaReferencia.refId`"
            v-bind="slotProps"
            readonly
            hidden
          />
        </ValidationWrapper>
        <ValidationWrapper
          v-slot="{ modelValue, ...slotProps }"
          :name="`${STRUCTURE_PATH}[${idx}].priloha.nazov`"
        >
          <IDSKInput
            :id="`${STRUCTURE_PATH}[${idx}].priloha.nazov`"
            :label="t('globalne.nazovPrilohy')"
            v-bind="slotProps"
            :model-value="modelValue"
          />
        </ValidationWrapper>
        <ValidationWrapper
          v-slot="slotProps"
          :name="`${STRUCTURE_PATH}[${idx}].priloha.poznamka`"
        >
          <IDSKTextarea
            :id="`${STRUCTURE_PATH}[${idx}].priloha.poznamka`"
            :label="t('globalne.poznamka')"
            :hint="t('globalne.volitelnaPoznamka')"
            v-bind="slotProps"
          />
        </ValidationWrapper>

        <DalsiePrilohy
          :name="`${STRUCTURE_PATH}[${idx}].priloha`"
          :array-position="idx"
        />
        <IDSKLink
          class="mb-6"
          href="#"
          no-visited-state
          @click.prevent="handleRemovePrilohy(idx)"
        >
          {{ t('globalne.odstranitPrilohu') }}
        </IDSKLink>
      </div>
    </template>
    <IDSKButton
      variant="secondary"
      class="mb-8"
      :data-test-id="`zoppm-prilohy-dalsie-add-priloha`"
      @click.prevent="() => push({})"
    >
      {{ t('globalne.pridatPrilohu') }}
    </IDSKButton>
  </template>
  <template v-else>
    <FileUploadButton variant="secondary" class="mb-8">
      {{ t('zoppm.step13.addAttachment') }}
    </FileUploadButton>
  </template>
</template>

<style lang="scss"></style>
